import React, { useEffect, useMemo, useState } from 'react';

// MUI (Mantis)
import { Box, Card, CardContent, CardHeader, Divider, FormControl, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';

import api from '../../api/axios';

// ===== AUTH: "apiKey" | "cookie" | "none"
const AUTH_MODE = 'apiKey';
const getAuthCfg = () => {
  switch (AUTH_MODE) {
    case 'apiKey':
      return { withCredentials: true, headers: { 'X-API-Key': 'secret123' } };
    case 'cookie':
      return { withCredentials: true };
    default:
      return {};
  }
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const eventBadgeStyle = (type) => {
  const t = String(type || '').toLowerCase();
  const isLinked = t === 'linked';
  const isDisconnected = t === 'disconnected';
  const bg = isLinked ? 'rgba(34,197,94,0.15)' : isDisconnected ? 'rgba(239,68,68,0.15)' : 'rgba(148,163,184,0.15)';
  const bd = isLinked ? 'rgba(34,197,94,0.35)' : isDisconnected ? 'rgba(239,68,68,0.35)' : 'rgba(148,163,184,0.35)';
  const fg = isLinked ? '#22c55e' : isDisconnected ? '#ef4444' : '#94a3b8';
  return { background: bg, borderColor: bd, color: fg };
};

// ================== NUMBER FORMAT ==================
const extractDigitsFromWaId = (wa) => {
  if (!wa) return '';
  return String(wa).split('@')[0].replace(/\D/g, '');
};

const group = (s, groups) => {
  let i = 0;
  const out = [];
  for (const g of groups) {
    if (i >= s.length) break;
    out.push(s.slice(i, i + g));
    i += g;
  }
  if (i < s.length) out.push(s.slice(i));
  return out.join(' ');
};

const formatE164Simple = (digits) => {
  const d = String(digits || '').replace(/\D/g, '');
  if (!d) return '-';

  // contoh requested:
  if (d.startsWith('62')) return `+62 ${group(d.slice(2), [3, 4, 4])}`.trim(); // ID
  if (d.startsWith('1')) return `+1 ${group(d.slice(1), [3, 3, 4])}`.trim(); // US/CA style
  if (d.startsWith('63')) return `+63 ${group(d.slice(2), [3, 3, 4])}`.trim(); // PH (simple)

  // fallback: anggap 1-2 digit country code
  const ccLen = d.length >= 11 ? 2 : 1;
  const cc = d.slice(0, ccLen);
  const rest = d.slice(ccLen);
  return `+${cc} ${group(rest, [3, 4, 4])}`.trim();
};

const formatWaNumber = (waIdOrNumber) => {
  const digits = extractDigitsFromWaId(waIdOrNumber);
  return formatE164Simple(digits);
};
// ===================================================

const pickUserId = (u) => u?.id ?? null;
const pickUsername = (u) => u?.username ?? null;

function Log() {
  const AUTH = useMemo(getAuthCfg, []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // server pagination (1-based)
  const [perPage, setPerPage] = useState(20);
  const [pageIndex, setPageIndex] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // search (client-side on current page, optional)
  const [query, setQuery] = useState('');

  // createdBy -> username map
  const [userMap, setUserMap] = useState({}); // { [id:number]: username:string }
  const [usersLoaded, setUsersLoaded] = useState(false);

  const buildPath = () => `/api/session-events/all/${perPage}/${pageIndex}`;

  const fetchUsers = async () => {
    try {
      const res = await api.get('/api/users/all/100/1', AUTH);
      const payload = res?.data || {};
      const list = Array.isArray(payload.users) ? payload.users : [];

      const map = {};
      list.forEach((u) => {
        const id = pickUserId(u);
        const name = pickUsername(u);
        if (id == null || !name) return;
        map[Number(id)] = String(name);
      });

      setUserMap(map);
      setUsersLoaded(true);
    } catch {
      setUserMap({});
      setUsersLoaded(true);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await api.get(buildPath(), AUTH);
      const payload = res?.data || {};
      const list = Array.isArray(payload.events) ? payload.events : [];

      setRows(list);

      const limit = Number(payload.limit) || perPage;
      const total = Number(payload.total) || 0;

      setTotalCount(total);
      setTotalPages(Math.max(1, Math.ceil(total / Math.max(1, limit))));
    } catch (e) {
      console.error('fetch session-events error:', e);
      setRows([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // load user map once
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // load events
  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage, pageIndex]);

  const createdByName = (createdBy) => {
    if (createdBy === undefined || createdBy === null) return '-';
    const id = Number(createdBy);
    if (id === 0) return 'system';
    return userMap[id] || `user#${id}`;
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const hay = [r?.ID, r?.SessionName, r?.Number, r?.EventType, r?.Reason, r?.CreatedAt, r?.CreatedBy]
        .map((x) => String(x ?? ''))
        .join(' ')
        .toLowerCase();

      return hay.includes(q);
    });
  }, [rows, query]);

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardHeader
          title="Session Events"
          action={
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <TextField
                size="small"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search (this page)..."
                sx={{ width: { xs: 240, md: 360 } }}
              />
            </Stack>
          }
        />

        <CardContent>
          {/* Table */}
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <Box component="thead">
                <Box component="tr">
                  {['ID', 'SESSION NAME', 'NUMBER', 'EVENT', 'REASON', 'CREATED AT'].map((h) => (
                    <Box
                      key={h}
                      component="th"
                      sx={{
                        p: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        textAlign: 'left',
                        whiteSpace: 'nowrap',
                        fontWeight: 600
                      }}
                    >
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box component="tbody">
                {filtered.length === 0 ? (
                  <Box component="tr">
                    <Box component="td" colSpan={7} sx={{ p: 1, opacity: 0.8 }}>
                      {loading ? 'Loading...' : query.trim() ? 'No matching events' : 'No events'}
                    </Box>
                  </Box>
                ) : (
                  filtered.map((d) => {
                    const st = eventBadgeStyle(d?.EventType);
                    const prettyNumber = formatWaNumber(d.Number);
                    const byName = createdByName(d.CreatedBy);

                    return (
                      <Box component="tr" key={d.ID}>
                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {d.ID}
                          </Typography>
                        </Box>

                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {d.SessionName || '-'}
                          </Typography>
                        </Box>

                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', opacity: 0.9 }}>
                            {prettyNumber}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.6, fontFamily: 'monospace' }}>
                            raw: {d.Number || '-'}
                          </Typography>
                        </Box>

                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 1.25,
                              py: 0.5,
                              borderRadius: 999,
                              border: '1px solid',
                              fontSize: 12,
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              ...st
                            }}
                          >
                            {d.EventType || '-'}
                          </Box>
                        </Box>

                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          {d.Reason ? (
                            <Typography variant="body2">{d.Reason}</Typography>
                          ) : (
                            <Typography variant="body2" sx={{ opacity: 0.6 }}>
                              -
                            </Typography>
                          )}
                        </Box>

                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {fmtDate(d.CreatedAt)}
                          </Typography>

                          {/* ✅ username dari users map */}
                          <Typography variant="caption" sx={{ opacity: 0.65 }}>
                            createdBy: <b>{byName}</b>
                            {!usersLoaded ? ' (loading users...)' : ''}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Pagination */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Total: {totalCount} • Page {pageIndex} / {totalPages}
              {query.trim() ? ` • Filter: ${query.trim()}` : ''}
            </Typography>

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                component="button"
                type="button"
                onClick={() => setPageIndex((p) => Math.max(1, p - 1))}
                disabled={pageIndex <= 1 || totalCount === 0}
                style={{
                  border: '1px solid rgba(255,255,255,0.18)',
                  background: 'transparent',
                  color: 'inherit',
                  borderRadius: 10,
                  padding: '6px 10px',
                  cursor: pageIndex <= 1 || totalCount === 0 ? 'not-allowed' : 'pointer',
                  opacity: pageIndex <= 1 || totalCount === 0 ? 0.5 : 1
                }}
              >
                Prev
              </Box>

              <Box
                component="button"
                type="button"
                onClick={() => setPageIndex((p) => Math.min(totalPages, p + 1))}
                disabled={pageIndex >= totalPages || totalCount === 0}
                style={{
                  border: '1px solid rgba(255,255,255,0.18)',
                  background: 'transparent',
                  color: 'inherit',
                  borderRadius: 10,
                  padding: '6px 10px',
                  cursor: pageIndex >= totalPages || totalCount === 0 ? 'not-allowed' : 'pointer',
                  opacity: pageIndex >= totalPages || totalCount === 0 ? 0.5 : 1
                }}
              >
                Next
              </Box>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setPageIndex(1);
                  }}
                >
                  {[10, 20, 50, 100, 200].map((n) => (
                    <MenuItem key={n} value={n}>
                      {n} / page
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

export default Log;
